import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { agentTickets, agentGmail } from '../../../services/api';
import {
  Inbox, Clock, RefreshCw, UserCheck, AlertTriangle,
  CheckCircle, XCircle, Mail, Archive, BellOff, RotateCcw,
  Search, Lock, ChevronDown, PlusCircle,
} from 'lucide-react';

// ── Helpers ────────────────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// SLA color — amber at 4h, red at 8h. Only meaningful for active (pending/reviewing) tickets.
function slaClass(dateStr, status) {
  if (!dateStr || !['pending', 'reviewing'].includes(status)) return 'text-muted';
  const hours = (Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60);
  if (hours >= 8) return 'text-danger fw-semibold';
  if (hours >= 4) return 'text-warning fw-semibold';
  return 'text-muted';
}

// Waiting-on-customer aging for sent tickets.
// Counts from updatedAt (≈ when status flipped to 'sent').
// Amber at 24h, red at 72h — no indicator returned for fresh sends under 4h.
function sentWaitInfo(updatedAt) {
  if (!updatedAt) return null;
  const hours = (Date.now() - new Date(updatedAt).getTime()) / (1000 * 60 * 60);
  const days  = Math.floor(hours / 24);
  if (hours >= 72) return { label: `No reply — ${days}d`, cls: 'text-danger fw-semibold',  badge: 'bg-danger'               };
  if (hours >= 24) return { label: `No reply — ${days}d`, cls: 'text-warning fw-semibold', badge: 'bg-warning text-dark'    };
  if (hours >= 4)  return { label: 'Awaiting reply',       cls: 'text-muted',               badge: null                      };
  return null; // sent very recently — no indicator needed
}

function fmtSnooze(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function snoozeUntil(duration) {
  const now = Date.now();
  switch (duration) {
    case '1h':       return new Date(now + 1  * 60 * 60 * 1000).toISOString();
    case '4h':       return new Date(now + 4  * 60 * 60 * 1000).toISOString();
    case 'tomorrow': return new Date(now + 24 * 60 * 60 * 1000).toISOString();
    case '3d':       return new Date(now + 3  * 24 * 60 * 60 * 1000).toISOString();
    case '1w':       return new Date(now + 7  * 24 * 60 * 60 * 1000).toISOString();
    default:         return null;
  }
}

function parseC7(raw) {
  if (!raw) return null;
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
}

// Abbreviate "orders@jeremywineco.com" → "orders@" for compact display
function shortInbox(email) {
  if (!email) return null;
  return email.includes('@') ? email.split('@')[0] + '@' : email;
}

// Whether a lock timestamp is still within the 30-min window
function isLockActive(lockedAt) {
  if (!lockedAt) return false;
  return (Date.now() - new Date(lockedAt).getTime()) < 30 * 60 * 1000;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function ConfidenceBar({ score }) {
  if (score == null) return null;
  const pct   = Math.round(score * 100);
  const color = score >= 0.7 ? 'bg-success' : score >= 0.4 ? 'bg-warning' : 'bg-danger';
  return (
    <div className="d-flex align-items-center gap-2">
      <div className="progress flex-grow-1" style={{ height: 5 }}>
        <div className={`progress-bar ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-muted" style={{ fontSize: 11, minWidth: 28 }}>{pct}%</span>
    </div>
  );
}

function EscalationBadge({ level }) {
  if (level === 'human_only') return <span className="badge bg-danger">HUMAN ONLY</span>;
  if (level === 'priority')   return <span className="badge bg-warning text-dark">PRIORITY</span>;
  return null;
}

function ClubBadge({ c7 }) {
  if (!c7) return null;
  if (c7.clubStatus === 'Active')    return <span className="badge bg-success" style={{ fontSize: 10 }}>Club Active</span>;
  if (c7.clubStatus === 'Cancelled') return <span className="badge bg-secondary" style={{ fontSize: 10 }}>Club Cancelled</span>;
  return null;
}

function InboxBadge({ email }) {
  if (!email) return null;
  return (
    <span
      className="badge bg-light text-secondary border"
      style={{ fontSize: 10, fontWeight: 500 }}
      title={email}
    >
      <Mail size={9} className="me-1" />
      {shortInbox(email)}
    </span>
  );
}

/**
 * GmailStatusStrip — always visible so staff know if email intake is working.
 */
function GmailStatusStrip({ gmailStatus, onConnect, isAdmin }) {
  if (!gmailStatus) return null;

  const { connected, email, expiresAt } = gmailStatus;
  const expiringSoon = connected && expiresAt
    ? (expiresAt - Date.now()) < 24 * 60 * 60 * 1000
    : false;

  if (!connected) {
    return (
      <div className="alert alert-danger py-2 px-3 d-flex align-items-center gap-2 mb-4" role="alert">
        <XCircle size={16} className="flex-shrink-0" />
        <span className="small flex-grow-1">
          <strong>Gmail not connected</strong> — the agent cannot receive new emails.
        </span>
        {isAdmin ? (
          <button className="btn btn-sm btn-danger" onClick={onConnect}>Connect Gmail</button>
        ) : (
          <span className="text-muted small fst-italic">Ask an admin to connect Gmail.</span>
        )}
      </div>
    );
  }

  if (expiringSoon) {
    return (
      <div className="alert alert-warning py-2 px-3 d-flex align-items-center gap-2 mb-4" role="alert">
        <AlertTriangle size={16} className="flex-shrink-0" />
        <span className="small flex-grow-1">
          <strong>Gmail token expiring soon</strong> — re-register watches to avoid interruption.
        </span>
        {isAdmin && (
          <Link to="/admin/agent/monitoring" className="btn btn-sm btn-warning">Manage →</Link>
        )}
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center gap-2 mb-4 px-1" style={{ fontSize: 13 }}>
      <CheckCircle size={14} className="text-success flex-shrink-0" />
      <span className="text-muted">
        Gmail connected · <strong className="text-dark">{email}</strong>
      </span>
      {isAdmin && (
        <Link to="/admin/agent/monitoring" className="text-muted ms-1" style={{ fontSize: 11 }}>
          Manage →
        </Link>
      )}
    </div>
  );
}

// ── Snooze dropdown ────────────────────────────────────────────────────────────

const SNOOZE_OPTIONS = [
  { value: '1h',       label: '1 hour' },
  { value: '4h',       label: '4 hours' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: '3d',       label: '3 days' },
  { value: '1w',       label: '1 week' },
];

function SnoozeDropdown({ ticketID, openId, setOpenId, onSnooze, disabled }) {
  const isOpen = openId === `snooze-${ticketID}`;
  return (
    <div className="dropdown" style={{ position: 'relative' }}>
      <button
        className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
        onClick={(e) => { e.stopPropagation(); setOpenId(isOpen ? null : `snooze-${ticketID}`); }}
        disabled={disabled}
        title="Snooze ticket"
      >
        <BellOff size={13} />
        Snooze
      </button>
      {isOpen && (
        <ul
          className="dropdown-menu show"
          style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1050, minWidth: 130 }}
          onClick={(e) => e.stopPropagation()}
        >
          {SNOOZE_OPTIONS.map((opt) => (
            <li key={opt.value}>
              <button
                className="dropdown-item"
                style={{ fontSize: 13 }}
                onClick={() => { onSnooze(ticketID, opt.value); setOpenId(null); }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Escalation dropdown ────────────────────────────────────────────────────────

const ESCALATION_OPTIONS = [
  { value: 'standard',   label: 'Standard',    cls: 'text-muted' },
  { value: 'priority',   label: 'Priority',    cls: 'text-warning' },
  { value: 'human_only', label: 'Human Only',  cls: 'text-danger' },
];

function EscalationDropdown({ ticketID, current, openId, setOpenId, onSet, disabled }) {
  const isOpen = openId === `escalation-${ticketID}`;
  return (
    <div className="dropdown" style={{ position: 'relative' }}>
      <button
        className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
        onClick={(e) => { e.stopPropagation(); setOpenId(isOpen ? null : `escalation-${ticketID}`); }}
        disabled={disabled}
        title="Override priority level"
        style={{ fontSize: 12 }}
      >
        <ChevronDown size={12} />
        Priority
      </button>
      {isOpen && (
        <ul
          className="dropdown-menu show"
          style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1050, minWidth: 140 }}
          onClick={(e) => e.stopPropagation()}
        >
          {ESCALATION_OPTIONS.map((opt) => (
            <li key={opt.value}>
              <button
                className={`dropdown-item d-flex align-items-center gap-2 ${opt.cls}`}
                style={{ fontSize: 13 }}
                onClick={() => { onSet(ticketID, opt.value); setOpenId(null); }}
                disabled={opt.value === current}
              >
                {opt.value === current && <span style={{ fontSize: 10 }}>✓</span>}
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Constants ──────────────────────────────────────────────────────────────────

const INTENT_LABELS = {
  order_request:     'Order Request',
  tasting_booking:   'Tasting Booking',
  club_change:       'Club Change',
  group_reservation: 'Group Reservation',
  unknown:           'Unknown',
};

const STATUS_TABS = [
  { value: 'pending',   label: 'Pending' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'approved',  label: 'Approved' },
  { value: 'sent',      label: 'Sent' },
  { value: 'rejected',  label: 'Rejected' },
  { value: 'snoozed',   label: 'Snoozed' },
  { value: 'archived',  label: 'Archived' },
];

const ACTIVE_TABS    = new Set(['pending', 'reviewing', 'approved', 'sent', 'rejected']);
const REOPENABLE_TABS = new Set(['snoozed', 'archived']);

// ── AgentQueue ─────────────────────────────────────────────────────────────────

export default function AgentQueue() {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const location   = useLocation();
  const isAdmin    = user?.role === 'admin';

  const [tickets,        setTickets]        = useState([]);
  const [stats,          setStats]          = useState(null);
  const [gmailStatus,    setGmailStatus]    = useState(null);
  const [pagination,     setPagination]     = useState({ total: 0, pages: 1 });
  const [loading,        setLoading]        = useState(true);
  const [refreshing,     setRefreshing]     = useState(false);
  const [error,          setError]          = useState('');
  const [assigning,      setAssigning]      = useState({});
  const [closing,        setClosing]        = useState({});
  const [reopening,      setReopening]      = useState({});
  const [snoozing,       setSnoozing]       = useState({});
  const [escalating,     setEscalating]     = useState({});

  // Dropdown open state — shared key: 'snooze-{id}' or 'escalation-{id}'
  const [openDropdownId, setOpenDropdownId] = useState(null);

  // Close confirmation modal
  const [closeModalTicket, setCloseModalTicket] = useState(null);

  // Filters
  const [statusFilter,     setStatusFilter]     = useState('pending');
  const [intentFilter,     setIntentFilter]     = useState('');
  const [escalationFilter, setEscalationFilter] = useState('');
  const [searchInput,      setSearchInput]      = useState('');
  const [searchQuery,      setSearchQuery]      = useState('');
  const [page,             setPage]             = useState(1);

  // Bulk selection
  const [selectedIDs, setSelectedIDs] = useState(new Set());
  const [bulkBusy,    setBulkBusy]    = useState(false);
  const [bulkMsg,     setBulkMsg]     = useState('');

  // Manual ticket creation
  const [showNewTicket,       setShowNewTicket]       = useState(false);
  const [newTicketBusy,       setNewTicketBusy]       = useState(false);
  const [newTicketError,      setNewTicketError]      = useState('');
  const [newTicketEmail,      setNewTicketEmail]      = useState('');
  const [newTicketName,       setNewTicketName]       = useState('');
  const [newTicketSubject,    setNewTicketSubject]     = useState('');
  const [newTicketBody,       setNewTicketBody]       = useState('');
  const [newTicketIntent,     setNewTicketIntent]     = useState('unknown');

  // Browser notification tracking — ref stores Set of ticketIDs already notified
  const notifiedIdsRef      = useRef(new Set());
  const prevPendingCountRef = useRef(null);

  // Debounce search input (400ms)
  const searchDebounceRef = useRef(null);
  useEffect(() => {
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchInput]);

  // Request browser notification permission once on mount (non-intrusive — fires only if not yet decided)
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Close all dropdowns when clicking elsewhere
  useEffect(() => {
    const close = () => setOpenDropdownId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  // Clear selection whenever tab or search changes
  useEffect(() => {
    setSelectedIDs(new Set());
  }, [statusFilter, searchQuery]);

  // ── Data fetchers ──────────────────────────────────────────────────────────

  const fetchGmailStatus = useCallback(async () => {
    try {
      const res = await agentGmail.status();
      setGmailStatus(res.data.data);
    } catch { /* Non-fatal */ }
  }, []);

  const fetchTickets = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = { status: statusFilter, page, limit: 25 };
      if (intentFilter)     params.intentType      = intentFilter;
      if (escalationFilter) params.escalationLevel = escalationFilter;
      if (searchQuery)      params.search          = searchQuery;

      const res = await agentTickets.list(params);
      setTickets(res.data.data.tickets || []);
      setPagination(res.data.data.pagination || { total: 0, pages: 1 });
      setError('');
    } catch {
      setError('Failed to load tickets. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter, intentFilter, escalationFilter, searchQuery, page]);

  const fetchStats = useCallback(async () => {
    if (user?.role !== 'admin') return;
    try {
      const res = await agentTickets.stats();
      setStats(res.data.data);
    } catch { /* Non-fatal */ }
  }, [user?.role]);

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchTickets();
    fetchStats();
    fetchGmailStatus();
  }, [fetchTickets, fetchStats, fetchGmailStatus]);

  // Re-fetch Gmail status after returning from OAuth flow
  useEffect(() => {
    if (new URLSearchParams(location.search).get('gmailConnected')) {
      fetchGmailStatus();
    }
  }, [location.search, fetchGmailStatus]);

  // Auto-refresh every 60 seconds (silent)
  useEffect(() => {
    const interval = setInterval(() => fetchTickets(true), 60000);
    return () => clearInterval(interval);
  }, [fetchTickets]);

  // Update document.title with pending count badge + fire notifications for new human_only tickets
  useEffect(() => {
    const pendingCount = stats?.byStatus?.find((s) => s.status === 'pending')?.count || 0;

    // Update browser tab title
    document.title = pendingCount > 0
      ? `(${pendingCount}) Agent Queue — JWC`
      : 'Agent Queue — JWC';

    // Fire desktop notifications for any new human_only pending tickets in the current view
    if (
      'Notification' in window &&
      Notification.permission === 'granted' &&
      statusFilter === 'pending'
    ) {
      const humanOnlyNew = tickets.filter(
        (t) =>
          t.escalationLevel === 'human_only' &&
          !notifiedIdsRef.current.has(t.ticketID)
      );

      humanOnlyNew.forEach((t) => {
        try {
          new Notification('Agent Queue — Human Attention Required', {
            body: `${t.customerName || t.customerEmail}: ${t.subject || '(no subject)'}`,
            icon: '/favicon.ico',
            tag:  `agent-ticket-${t.ticketID}`,
          });
          notifiedIdsRef.current.add(t.ticketID);
        } catch { /* notification blocked or unsupported */ }
      });
    }

    return () => {
      document.title = 'Agent Queue — JWC';
    };
  }, [tickets, stats, statusFilter]);

  // Re-check Gmail status every 2 minutes
  useEffect(() => {
    const interval = setInterval(fetchGmailStatus, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchGmailStatus]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleRefresh = () => {
    setRefreshing(true);
    fetchTickets();
    fetchStats();
    fetchGmailStatus();
  };

  const handleConnectGmail = async () => {
    try {
      const res = await agentGmail.authUrl();
      window.location.href = res.data.data.url;
    } catch {
      setError('Failed to generate Gmail authorization URL. Check backend logs.');
    }
  };

  const handleAssignToMe = async (ticketID) => {
    setAssigning((prev) => ({ ...prev, [ticketID]: true }));
    try {
      await agentTickets.assign(ticketID, user.id);
      fetchTickets(true);
    } catch { /* silent */ } finally {
      setAssigning((prev) => ({ ...prev, [ticketID]: false }));
    }
  };

  const handleClose = async (ticketID) => {
    setCloseModalTicket(null);
    setClosing((prev) => ({ ...prev, [ticketID]: true }));
    try {
      await agentTickets.close(ticketID);
      fetchTickets(true);
      fetchStats();
    } catch {
      setError('Failed to close ticket.');
    } finally {
      setClosing((prev) => ({ ...prev, [ticketID]: false }));
    }
  };

  const handleReopen = async (ticketID) => {
    setReopening((prev) => ({ ...prev, [ticketID]: true }));
    try {
      await agentTickets.reopen(ticketID);
      fetchTickets(true);
      fetchStats();
    } catch {
      setError('Failed to reopen ticket.');
    } finally {
      setReopening((prev) => ({ ...prev, [ticketID]: false }));
    }
  };

  const handleSnooze = async (ticketID, duration) => {
    const until = snoozeUntil(duration);
    if (!until) return;
    setSnoozing((prev) => ({ ...prev, [ticketID]: true }));
    try {
      await agentTickets.snooze(ticketID, until);
      fetchTickets(true);
    } catch {
      setError('Failed to snooze ticket.');
    } finally {
      setSnoozing((prev) => ({ ...prev, [ticketID]: false }));
    }
  };

  const handleUnsnooze = async (ticketID) => {
    setReopening((prev) => ({ ...prev, [ticketID]: true }));
    try {
      await agentTickets.unsnooze(ticketID);
      fetchTickets(true);
    } catch {
      setError('Failed to unsnooze ticket.');
    } finally {
      setReopening((prev) => ({ ...prev, [ticketID]: false }));
    }
  };

  const handleSetEscalation = async (ticketID, level) => {
    setEscalating((prev) => ({ ...prev, [ticketID]: true }));
    try {
      await agentTickets.setEscalation(ticketID, level);
      fetchTickets(true);
    } catch {
      setError('Failed to update priority.');
    } finally {
      setEscalating((prev) => ({ ...prev, [ticketID]: false }));
    }
  };

  // ── Bulk actions ───────────────────────────────────────────────────────────

  const toggleSelect = (id) => {
    setSelectedIDs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIDs.size === tickets.length) {
      setSelectedIDs(new Set());
    } else {
      setSelectedIDs(new Set(tickets.map((t) => t.ticketID)));
    }
  };

  const handleBulkClose = async () => {
    if (!selectedIDs.size) return;
    setBulkBusy(true);
    setBulkMsg('');
    try {
      await agentTickets.bulk('close', Array.from(selectedIDs));
      setBulkMsg(`Closed ${selectedIDs.size} ticket${selectedIDs.size !== 1 ? 's' : ''}.`);
      setSelectedIDs(new Set());
      fetchTickets(true);
      fetchStats();
    } catch {
      setError('Bulk close failed. Please try again.');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkAssignMe = async () => {
    if (!selectedIDs.size) return;
    setBulkBusy(true);
    setBulkMsg('');
    try {
      await agentTickets.bulk('assign_me', Array.from(selectedIDs));
      setBulkMsg(`Assigned ${selectedIDs.size} ticket${selectedIDs.size !== 1 ? 's' : ''} to you.`);
      setSelectedIDs(new Set());
      fetchTickets(true);
    } catch {
      setError('Bulk assign failed. Please try again.');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleStatusTab = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const resetNewTicketForm = () => {
    setNewTicketEmail('');
    setNewTicketName('');
    setNewTicketSubject('');
    setNewTicketBody('');
    setNewTicketIntent('unknown');
    setNewTicketError('');
  };

  const handleCreateManual = async () => {
    if (!newTicketEmail.trim() || !newTicketSubject.trim() || !newTicketBody.trim()) {
      setNewTicketError('Customer email, subject, and message are required.');
      return;
    }
    setNewTicketBusy(true);
    setNewTicketError('');
    try {
      const res = await agentTickets.createManual({
        customerEmail: newTicketEmail.trim(),
        customerName:  newTicketName.trim() || undefined,
        subject:       newTicketSubject.trim(),
        bodyText:      newTicketBody.trim(),
        intentType:    newTicketIntent,
      });
      const ticketID = res.data.data.ticketID;
      setShowNewTicket(false);
      resetNewTicketForm();
      navigate(`/admin/agent/tickets/${ticketID}`);
    } catch (err) {
      setNewTicketError(err.response?.data?.message || 'Failed to create ticket. Please try again.');
    } finally {
      setNewTicketBusy(false);
    }
  };

  // ── Stat bar values ────────────────────────────────────────────────────────

  const statPending    = stats?.byStatus?.find((s) => s.status === 'pending')?.count     ?? '—';
  const statPriority   = stats?.byEscalation?.find((e) => e.escalationLevel === 'priority')?.count   ?? '—';
  const statHuman      = stats?.byEscalation?.find((e) => e.escalationLevel === 'human_only')?.count ?? '—';
  const statConfidence = stats?.avgConfidence
    ? `${Math.round(parseFloat(stats.avgConfidence) * 100)}%`
    : '—';

  // ── Empty state ────────────────────────────────────────────────────────────

  function EmptyState() {
    if (statusFilter === 'archived') {
      return (
        <div className="text-center py-5 text-muted">
          <Archive size={40} className="mb-3 opacity-50" />
          <h6>No archived tickets</h6>
          <p className="small mb-0">Closed tickets will appear here.</p>
        </div>
      );
    }
    if (statusFilter === 'snoozed') {
      return (
        <div className="text-center py-5 text-muted">
          <BellOff size={40} className="mb-3 opacity-50" />
          <h6>No snoozed tickets</h6>
          <p className="small mb-0">Snoozed tickets will reappear when their time is up.</p>
        </div>
      );
    }
    if (searchQuery) {
      return (
        <div className="text-center py-5 text-muted">
          <Search size={40} className="mb-3 opacity-50" />
          <h6>No results for "{searchQuery}"</h6>
          <p className="small mb-0">Try a different name, email, or subject.</p>
        </div>
      );
    }
    if (!gmailStatus?.connected) {
      return (
        <div className="text-center py-5">
          <XCircle size={40} className="mb-3 text-danger opacity-75" />
          <h6>Gmail is not connected</h6>
          <p className="text-muted small mb-3">
            The agent cannot receive emails until a Gmail account is connected and inboxes are configured.
          </p>
          {isAdmin ? (
            <button className="btn btn-primary btn-sm" onClick={handleConnectGmail}>
              Connect Gmail Account
            </button>
          ) : (
            <p className="text-muted small mb-0">Contact an admin to connect Gmail.</p>
          )}
        </div>
      );
    }
    return (
      <div className="text-center py-5 text-muted">
        <Inbox size={40} className="mb-3 opacity-50" />
        <h6>All caught up</h6>
        <p className="small mb-0">No {statusFilter} tickets at this time.</p>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Page header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Agent Queue</h4>
          <p className="text-muted small mb-0">AI-assisted customer service review</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-sm btn-primary d-flex align-items-center gap-1"
            onClick={() => { resetNewTicketForm(); setShowNewTicket(true); }}
            title="Log a walk-in, phone call, or non-email interaction"
          >
            <PlusCircle size={14} /> New Ticket
          </button>
          <button
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Gmail connection status */}
      <GmailStatusStrip gmailStatus={gmailStatus} onConnect={handleConnectGmail} isAdmin={isAdmin} />

      {/* Stat bar — admin only */}
      {stats && (
        <div className="row g-3 mb-4">
          {[
            { label: 'Pending',        value: statPending,    color: 'primary' },
            { label: 'Priority',       value: statPriority,   color: 'warning' },
            { label: 'Human Only',     value: statHuman,      color: 'danger'  },
            { label: 'Avg Confidence', value: statConfidence, color: 'success' },
          ].map(({ label, value, color }) => (
            <div key={label} className="col-6 col-md-3">
              <div className="card border-0 shadow-sm">
                <div className="card-body py-3">
                  <div className={`text-${color} fw-bold fs-4`}>{value}</div>
                  <div className="text-muted small">{label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body py-2">
          {/* Row 1: status tabs */}
          <div className="d-flex align-items-center gap-3 flex-wrap mb-2">
            <ul className="nav nav-pills gap-1 flex-nowrap overflow-auto">
              {STATUS_TABS.map((tab) => (
                <li key={tab.value} className="nav-item">
                  <button
                    className={`nav-link py-1 px-3 ${statusFilter === tab.value ? 'active' : ''}`}
                    onClick={() => handleStatusTab(tab.value)}
                    style={{ fontSize: 13 }}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Row 2: search + intent/escalation filters */}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {/* Search box */}
            <div className="input-group input-group-sm" style={{ maxWidth: 260 }}>
              <span className="input-group-text bg-white border-end-0">
                <Search size={13} className="text-muted" />
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder="Search tickets…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                style={{ fontSize: 13 }}
              />
              {searchInput && (
                <button
                  className="btn btn-outline-secondary border-start-0"
                  onClick={() => { setSearchInput(''); setSearchQuery(''); }}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>

            {/* Intent + escalation filters — hide on snoozed/archived */}
            {!REOPENABLE_TABS.has(statusFilter) && (
              <>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto', minWidth: 150 }}
                  value={intentFilter}
                  onChange={(e) => { setIntentFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All Intents</option>
                  <option value="order_request">Order Request</option>
                  <option value="tasting_booking">Tasting Booking</option>
                  <option value="club_change">Club Change</option>
                  <option value="group_reservation">Group Reservation</option>
                  <option value="unknown">Unknown</option>
                </select>

                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto', minWidth: 140 }}
                  value={escalationFilter}
                  onChange={(e) => { setEscalationFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All Escalations</option>
                  <option value="standard">Standard</option>
                  <option value="priority">Priority</option>
                  <option value="human_only">Human Only</option>
                </select>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bulk action bar — shown when any tickets are selected */}
      {selectedIDs.size > 0 && (
        <div className="alert alert-light border d-flex align-items-center gap-3 py-2 mb-3">
          <span className="fw-semibold small">{selectedIDs.size} selected</span>
          <button
            className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1"
            onClick={handleBulkAssignMe}
            disabled={bulkBusy}
          >
            {bulkBusy ? <span className="spinner-border spinner-border-sm" /> : <><UserCheck size={13} /> Assign to Me</>}
          </button>
          {ACTIVE_TABS.has(statusFilter) && (
            <button
              className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
              onClick={handleBulkClose}
              disabled={bulkBusy}
            >
              {bulkBusy ? <span className="spinner-border spinner-border-sm" /> : <><Archive size={13} /> Close All</>}
            </button>
          )}
          <button
            className="btn btn-sm btn-link text-muted p-0 ms-auto"
            onClick={() => setSelectedIDs(new Set())}
          >
            Clear
          </button>
        </div>
      )}

      {/* Bulk success message */}
      {bulkMsg && (
        <div className="alert alert-success py-2 d-flex justify-content-between align-items-center mb-3">
          {bulkMsg}
          <button type="button" className="btn-close" onClick={() => setBulkMsg('')} />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="alert alert-danger py-2 d-flex justify-content-between align-items-center">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')} />
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>

      /* Empty state */
      ) : tickets.length === 0 ? (
        <EmptyState />

      /* Ticket cards */
      ) : (
        <>
          {/* Select-all row — shown on active tabs with results */}
          {ACTIVE_TABS.has(statusFilter) && (
            <div className="d-flex align-items-center gap-2 mb-2 px-1">
              <input
                type="checkbox"
                className="form-check-input mt-0"
                checked={selectedIDs.size === tickets.length && tickets.length > 0}
                ref={(el) => { if (el) el.indeterminate = selectedIDs.size > 0 && selectedIDs.size < tickets.length; }}
                onChange={toggleSelectAll}
                title="Select all"
              />
              <span className="text-muted small">
                {selectedIDs.size > 0
                  ? `${selectedIDs.size} of ${tickets.length} selected`
                  : `${tickets.length} ticket${tickets.length !== 1 ? 's' : ''}`}
              </span>
            </div>
          )}

          <div className="d-flex flex-column gap-3">
            {tickets.map((ticket) => {
              const c7         = parseC7(ticket.c7ContextSnapshot);
              const isAssigned = ticket.assignedRepID === user?.id;
              const isArchived = REOPENABLE_TABS.has(statusFilter);
              const isBusy     = !!closing[ticket.ticketID] || !!reopening[ticket.ticketID] || !!snoozing[ticket.ticketID] || !!escalating[ticket.ticketID];
              const isSelected = selectedIDs.has(ticket.ticketID);
              const lockActive = ticket.lockedBy && ticket.lockedBy !== user?.id && isLockActive(ticket.lockedAt);

              return (
                <div
                  key={ticket.ticketID}
                  className={`card border-0 shadow-sm ${isSelected ? 'border border-primary' : ''}`}
                  style={isSelected ? { outline: '2px solid var(--bs-primary)', outlineOffset: '-1px' } : {}}
                >
                  <div className="card-body">
                    {/* Row 1: checkbox (active only) + name + badges + age */}
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="d-flex align-items-start gap-2 flex-wrap">
                        {ACTIVE_TABS.has(statusFilter) && (
                          <input
                            type="checkbox"
                            className="form-check-input mt-1 flex-shrink-0"
                            checked={isSelected}
                            onChange={() => toggleSelect(ticket.ticketID)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <span className="fw-semibold">
                            {ticket.customerName || ticket.customerEmail}
                          </span>
                          {ticket.customerName && (
                            <span className="text-muted small">{ticket.customerEmail}</span>
                          )}
                          <EscalationBadge level={ticket.escalationLevel} />
                          <ClubBadge c7={c7} />
                          <InboxBadge email={ticket.inboxEmail} />
                          {/* Lock indicator — shows when another rep has this open */}
                          {lockActive && (
                            <span className="badge bg-warning text-dark d-flex align-items-center gap-1" style={{ fontSize: 10 }}>
                              <Lock size={9} />
                              {ticket.lockedByName || 'Being reviewed'}
                            </span>
                          )}
                          {/* Snoozed indicator */}
                          {statusFilter === 'snoozed' && ticket.snoozedUntil && (
                            <span className="badge bg-light text-secondary border" style={{ fontSize: 10 }}>
                              <BellOff size={9} className="me-1" />
                              Until {fmtSnooze(ticket.snoozedUntil)}
                            </span>
                          )}
                          {/* Archived indicator */}
                          {statusFilter === 'archived' && (
                            <span className="badge bg-secondary" style={{ fontSize: 10 }}>
                              Archived · {ticket.status}
                            </span>
                          )}
                          {/* No-reply badge — shown on sent tab when wait is ≥ 24h */}
                          {ticket.status === 'sent' && (() => {
                            const wait = sentWaitInfo(ticket.updatedAt);
                            return wait?.badge ? (
                              <span className={`badge ${wait.badge}`} style={{ fontSize: 10 }}>
                                {wait.label}
                              </span>
                            ) : null;
                          })()}
                        </div>
                      </div>
                      {/* Age indicator — SLA for active tickets, wait-time for sent */}
                      {ticket.status === 'sent' ? (() => {
                        const wait = sentWaitInfo(ticket.updatedAt);
                        return (
                          <div className={`d-flex align-items-center gap-1 small text-nowrap flex-shrink-0 ms-2 ${wait?.cls || 'text-muted'}`}>
                            <Clock size={12} />
                            {wait ? wait.label : `Sent ${timeAgo(ticket.updatedAt)}`}
                          </div>
                        );
                      })() : (
                        <div className={`d-flex align-items-center gap-1 small text-nowrap flex-shrink-0 ms-2 ${slaClass(ticket.createdAt, ticket.status)}`}>
                          <Clock size={12} />
                          {timeAgo(ticket.createdAt)}
                        </div>
                      )}
                    </div>

                    {/* Row 2: intent + assignment */}
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>
                        {INTENT_LABELS[ticket.intentType] || ticket.intentType || 'Unknown'}
                      </span>
                      <span className="text-muted small">
                        {ticket.assignedRepName
                          ? <><UserCheck size={12} className="me-1" />{ticket.assignedRepName}</>
                          : <span className="fst-italic">Unassigned</span>}
                      </span>
                    </div>

                    {/* Row 3: subject + body preview */}
                    <p className="mb-1 small fw-semibold text-truncate">{ticket.subject}</p>
                    <p className="text-muted small mb-2" style={{ lineHeight: 1.5 }}>
                      {(ticket.bodyPreview || '').slice(0, 120)}
                      {(ticket.bodyPreview || '').length > 120 ? '…' : ''}
                    </p>

                    {/* Sent reply preview — shown on the sent tab when finalBodyPreview is available */}
                    {ticket.status === 'sent' && ticket.finalBodyPreview && (
                      <div className="border-start border-3 border-success ps-2 mb-2">
                        <div className="text-muted mb-1" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Reply sent
                        </div>
                        <p className="mb-0 small" style={{ lineHeight: 1.5, color: '#444', fontStyle: 'italic' }}>
                          {ticket.finalBodyPreview.slice(0, 110)}
                          {ticket.finalBodyPreview.length >= 110 ? '…' : ''}
                        </p>
                      </div>
                    )}

                    {/* Row 4: confidence bar */}
                    {ticket.confidenceScore != null && (
                      <div className="mb-3" style={{ maxWidth: 200 }}>
                        <div className="text-muted mb-1" style={{ fontSize: 11 }}>Confidence</div>
                        <ConfidenceBar score={ticket.confidenceScore} />
                      </div>
                    )}

                    {/* Row 5: action buttons */}
                    <div className="d-flex gap-2 flex-wrap">
                      {isArchived ? (
                        /* Archived / snoozed tabs */
                        <>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate(`/admin/agent/tickets/${ticket.ticketID}`)}
                          >
                            View
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                            onClick={() => statusFilter === 'snoozed'
                              ? handleUnsnooze(ticket.ticketID)
                              : handleReopen(ticket.ticketID)
                            }
                            disabled={isBusy}
                          >
                            {isBusy
                              ? <span className="spinner-border spinner-border-sm" />
                              : <><RotateCcw size={13} /> {statusFilter === 'snoozed' ? 'Wake Up' : 'Reopen'}</>}
                          </button>
                        </>
                      ) : (
                        /* Active tabs */
                        <>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => navigate(`/admin/agent/tickets/${ticket.ticketID}`)}
                          >
                            Review
                          </button>
                          {!isAssigned && (
                            <button
                              className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                              onClick={() => handleAssignToMe(ticket.ticketID)}
                              disabled={!!assigning[ticket.ticketID]}
                            >
                              {assigning[ticket.ticketID]
                                ? <span className="spinner-border spinner-border-sm" />
                                : <><UserCheck size={13} /> Assign to Me</>}
                            </button>
                          )}
                          <EscalationDropdown
                            ticketID={ticket.ticketID}
                            current={ticket.escalationLevel}
                            openId={openDropdownId}
                            setOpenId={setOpenDropdownId}
                            onSet={handleSetEscalation}
                            disabled={isBusy}
                          />
                          <SnoozeDropdown
                            ticketID={ticket.ticketID}
                            openId={openDropdownId}
                            setOpenId={setOpenDropdownId}
                            onSnooze={handleSnooze}
                            disabled={isBusy}
                          />
                          <button
                            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                            onClick={() => setCloseModalTicket(ticket)}
                            disabled={isBusy}
                            title="Close and archive this ticket"
                          >
                            {closing[ticket.ticketID]
                              ? <span className="spinner-border spinner-border-sm" />
                              : <><Archive size={13} /> Close</>}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage((p) => p - 1)}>Previous</button>
                </li>
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
                  <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                  </li>
                ))}
                <li className={`page-item ${page === pagination.pages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => setPage((p) => p + 1)}>Next</button>
                </li>
              </ul>
            </div>
          )}
        </>
      )}

      {/* Total count */}
      {!loading && tickets.length > 0 && (
        <p className="text-muted small text-center mt-3 mb-0">
          {pagination.total} ticket{pagination.total !== 1 ? 's' : ''} — auto-refreshes every 60s
        </p>
      )}

      {/* ── Close Confirmation Modal ─────────────────────────────────────────── */}
      {closeModalTicket && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setCloseModalTicket(null); }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title d-flex align-items-center gap-2">
                  <Archive size={16} /> Close Ticket
                </h6>
                <button type="button" className="btn-close" onClick={() => setCloseModalTicket(null)} />
              </div>
              <div className="modal-body">
                <p className="mb-2">
                  Close ticket from{' '}
                  <strong>{closeModalTicket.customerName || closeModalTicket.customerEmail}</strong>?
                </p>
                <p className="text-muted small mb-0">
                  It will be hidden from the queue and moved to the <strong>Archived</strong> tab.
                  You can reopen it any time.
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setCloseModalTicket(null)}>Cancel</button>
                <button
                  className="btn btn-dark d-flex align-items-center gap-1"
                  onClick={() => handleClose(closeModalTicket.ticketID)}
                >
                  <Archive size={14} /> Close Ticket
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── New Ticket Modal ─────────────────────────────────────────────────── */}
      {showNewTicket && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowNewTicket(false); }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title d-flex align-items-center gap-2">
                  <PlusCircle size={16} /> New Manual Ticket
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowNewTicket(false)}
                  disabled={newTicketBusy}
                />
              </div>
              <div className="modal-body">
                <p className="text-muted small mb-3">
                  Log a walk-in, phone call, or other non-email interaction. The AI will draft a reply suggestion you can send or use as notes.
                </p>

                <div className="mb-3">
                  <label className="form-label small fw-semibold">Customer Email <span className="text-danger">*</span></label>
                  <input
                    type="email"
                    className="form-control form-control-sm"
                    value={newTicketEmail}
                    onChange={(e) => setNewTicketEmail(e.target.value)}
                    placeholder="customer@example.com"
                    disabled={newTicketBusy}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-semibold">Customer Name <span className="text-muted fw-normal">(optional)</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={newTicketName}
                    onChange={(e) => setNewTicketName(e.target.value)}
                    placeholder="Jane Smith"
                    disabled={newTicketBusy}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-semibold">Subject <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={newTicketSubject}
                    onChange={(e) => setNewTicketSubject(e.target.value)}
                    placeholder="e.g. Walk-in tasting room inquiry"
                    disabled={newTicketBusy}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-semibold">Message / Notes <span className="text-danger">*</span></label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={4}
                    value={newTicketBody}
                    onChange={(e) => setNewTicketBody(e.target.value)}
                    placeholder="What did the customer say or ask? Include any relevant context…"
                    disabled={newTicketBusy}
                    style={{ resize: 'vertical' }}
                  />
                </div>

                <div className="mb-0">
                  <label className="form-label small fw-semibold">Intent Type</label>
                  <select
                    className="form-select form-select-sm"
                    value={newTicketIntent}
                    onChange={(e) => setNewTicketIntent(e.target.value)}
                    disabled={newTicketBusy}
                  >
                    <option value="unknown">Unknown / General</option>
                    <option value="order_request">Order Request</option>
                    <option value="tasting_booking">Tasting Booking</option>
                    <option value="club_change">Club Change</option>
                    <option value="group_reservation">Group Reservation</option>
                  </select>
                </div>

                {newTicketError && (
                  <div className="alert alert-danger py-2 mt-3 mb-0">{newTicketError}</div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowNewTicket(false)}
                  disabled={newTicketBusy}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary d-flex align-items-center gap-1"
                  onClick={handleCreateManual}
                  disabled={newTicketBusy || !newTicketEmail.trim() || !newTicketSubject.trim() || !newTicketBody.trim()}
                >
                  {newTicketBusy
                    ? <><span className="spinner-border spinner-border-sm" /> Creating…</>
                    : <><PlusCircle size={14} /> Create Ticket</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
